import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import OnboardingHeader from '../components/onboarding/OnboardingHeader';
import OnboardingFooter from '../components/onboarding/OnboardingFooter';
import OnboardingPage from '../components/onboarding/OnboardingPage';
import PlanSelection from '../components/onboarding/PlanSelection';
import { API, STORAGE_KEY } from '../components/onboarding/constants';
import { darkenHex, hexBrightness } from '../components/onboarding/colorUtils';

export default function Onboarding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, updateUser, register, logout } = useAuth();

  // Load saved progress (only for unregistered users)
  const saved = !user ? (() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}; } catch { return {}; }
  })() : {};

  const [page, setPage] = useState(saved.page || 1);
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState(null);
  const [onboardingConfig, setOnboardingConfig] = useState(null);

  // Derive enabled pages and total from config
  const enabledPages = onboardingConfig?.pages?.filter(p => p.enabled) || [];
  // When the embedded payment_capture page is enabled, IT becomes the final
  // step — we skip the implicit hosted-redirect PlanSelection page.
  const hasEmbeddedPayment = enabledPages.some(p => p.type === 'payment_capture');
  const TOTAL_PAGES = hasEmbeddedPayment ? enabledPages.length : enabledPages.length + 1;

  // Registration fields (for unregistered users on plan page)
  const [email, setEmail] = useState(saved.email || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // File upload states
  const [badgeFile, setBadgeFile] = useState(null);
  const [badgePreview, setBadgePreview] = useState(null);

  // Page transition direction ('forward' or 'back') and a re-mount key for the logo
  const [direction, setDirection] = useState('forward');
  const [logoSpinKey, setLogoSpinKey] = useState(0);

  // Dynamic data store for all onboarding fields. Counter pages (years_coaching,
  // teams_coached) start undefined so the CounterBlock displays 0 — user must
  // tap + to set their actual number. This avoids defaulting users to a value
  // that may be wrong for them.
  const [formData, setFormData] = useState({
    years_coaching: saved.yearsCoaching,
    coaching_levels: saved.coachingLevels || [],
    teams_coached: saved.teamsCoached,
    age_groups: saved.ageGroups || [],
    display_name: saved.displayName || user?.name?.split(' ')[0] || '',
    team_name: saved.teamName || '',
    club_badge: '',
    club_colours: { primary: saved.primaryColor || '#064E3B', secondary: saved.secondaryColor || '#F8FAFC' },
    ...saved.customFields,
  });

  // Convenience accessors for backward compat
  const yearsCoaching = formData.years_coaching;
  const coachingLevels = formData.coaching_levels;
  const teamsCoached = formData.teams_coached;
  const ageGroups = formData.age_groups;
  const displayName = formData.display_name;
  const teamName = formData.team_name;
  const clubBadgeUrl = formData.club_badge;
  const primaryColor = formData.club_colours?.primary || '#064E3B';
  const secondaryColor = formData.club_colours?.secondary || '#F8FAFC';

  const setField = (key, value) => setFormData(prev => ({ ...prev, [key]: value }));
  const setYearsCoaching = (fn) => setFormData(prev => ({ ...prev, years_coaching: typeof fn === 'function' ? fn(prev.years_coaching) : fn }));
  const setCoachingLevels = (fn) => setFormData(prev => ({ ...prev, coaching_levels: typeof fn === 'function' ? fn(prev.coaching_levels) : fn }));
  const setTeamsCoached = (fn) => setFormData(prev => ({ ...prev, teams_coached: typeof fn === 'function' ? fn(prev.teams_coached) : fn }));
  const setAgeGroups = (fn) => setFormData(prev => ({ ...prev, age_groups: typeof fn === 'function' ? fn(prev.age_groups) : fn }));
  const setDisplayName = (val) => setFormData(prev => ({ ...prev, display_name: val }));
  const setTeamName = (val) => setFormData(prev => ({ ...prev, team_name: val }));
  const setClubBadgeUrl = (val) => setFormData(prev => ({ ...prev, club_badge: val }));
  const setPrimaryColor = (val) => setFormData(prev => ({ ...prev, club_colours: { ...prev.club_colours, primary: val } }));
  const setSecondaryColor = (val) => setFormData(prev => ({ ...prev, club_colours: { ...prev.club_colours, secondary: val } }));

  // Auto-save progress to localStorage (unregistered users only, never save passwords)
  useEffect(() => {
    if (user) return;
    const progress = {
      page, email,
      yearsCoaching: formData.years_coaching,
      coachingLevels: formData.coaching_levels,
      teamsCoached: formData.teams_coached,
      ageGroups: formData.age_groups,
      displayName: formData.display_name,
      teamName: formData.team_name,
      primaryColor: formData.club_colours?.primary,
      secondaryColor: formData.club_colours?.secondary,
      customFields: formData,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
  }, [user, page, email, formData]);

  // Show resume toast if progress was restored
  useEffect(() => {
    if (!user && saved.page && saved.page > 1) {
      toast.info('Welcome back! We\'ve restored your progress.', { duration: 3000 });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch onboarding config + plans
  useEffect(() => {
    axios.get(`${API}/onboarding-config`).then(r => setOnboardingConfig(r.data)).catch(() => {});
    axios.get(`${API}/stripe/plans`).then(r => setPlans(r.data.plans)).catch(() => {});
  }, []);

  // Handle return from cancelled checkout
  useEffect(() => {
    if (searchParams.get('cancelled') === 'true' && searchParams.get('step') === 'plan') {
      setPage(TOTAL_PAGES);
      toast.info('Payment cancelled. You can choose a different plan.');
    }
  }, [searchParams]);

  // Preview shortcut — jump straight to the payment_capture page when the
  // URL has ?step=payment. Useful for previewing the embedded form without
  // walking through every onboarding step. No-op if payment_capture is not
  // enabled in the CMS. Fires ONCE per mount so Back button doesn't bounce
  // the user right back to the payment page.
  const hasJumpedToPaymentRef = useRef(false);
  useEffect(() => {
    if (hasJumpedToPaymentRef.current) return;
    if (searchParams.get('step') === 'payment' && enabledPages.length > 0) {
      const idx = enabledPages.findIndex(p => p.type === 'payment_capture');
      if (idx >= 0) {
        hasJumpedToPaymentRef.current = true;
        setPage(idx + 1);
      }
    }
  }, [searchParams, enabledPages]);

  const uploadFile = async (file) => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Session expired. Please log in again.');
    const fd = new FormData();
    fd.append('file', file);
    const res = await axios.post(`${API}/upload-image`, fd, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return res.data.image_url;
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
      toast.error('Please select a valid image (JPEG, PNG, GIF, or WebP)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    setBadgeFile(file);
    setBadgePreview(URL.createObjectURL(file));
  };

  const removeBadge = () => {
    if (badgePreview) URL.revokeObjectURL(badgePreview);
    setBadgeFile(null);
    setBadgePreview(null);
    setClubBadgeUrl('');
  };

  // Complete onboarding (save profile + setup) then show plan page
  const completeOnboarding = async () => {
    if (!displayName.trim()) {
      toast.error('Display name is required');
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Session expired. Please log in again.');
      navigate('/login', { replace: true });
      return;
    }
    setLoading(true);
    try {
      let badge = clubBadgeUrl;
      if (badgeFile) {
        badge = await uploadFile(badgeFile);
      }

      const res = await axios.post(`${API}/auth/onboarding`, {
        years_coaching: yearsCoaching,
        coaching_levels: coachingLevels,
        teams_coached: teamsCoached,
        age_groups: ageGroups,
        display_name: displayName.trim(),
        team_name: teamName.trim() || null,
        club_badge: badge || null,
        button_color: primaryColor,
        menu_color_secondary: secondaryColor,
      }, { headers: { Authorization: `Bearer ${token}` } });

      updateUser(res.data);
      setPage(TOTAL_PAGES); // Go to plan selection
    } catch (err) {
      if (err.response?.status === 401 || err.message?.includes('Session expired')) {
        toast.error('Session expired. Please log in again.');
        navigate('/login', { replace: true });
      } else {
        toast.error(err.response?.data?.detail || 'Failed to save profile');
      }
    } finally {
      setLoading(false);
    }
  };

  // Initiate Stripe checkout (authenticated user)
  const selectPlan = async (planId, referralCode) => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Session expired. Please log in again.');
      navigate('/login', { replace: true });
      return;
    }
    setLoading(true);
    try {
      const origin = window.location.origin;
      const res = await axios.post(`${API}/stripe/create-checkout`, {
        plan_id: planId,
        origin_url: origin,
        referral_code: referralCode || null,
      }, { headers: { Authorization: `Bearer ${token}` } });

      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
      setLoading(false);
    }
  };

  // Full flow for unregistered users: register → onboarding → checkout
  const handlePublicPlanSelection = async (planId, referralCode) => {
    if (!email.trim()) { toast.error('Please enter your email address'); return; }
    if (!password) { toast.error('Please enter a password'); return; }
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (password !== confirmPassword) { toast.error('Passwords do not match'); return; }

    setLoading(true);
    try {
      // 1. Register account (also logs in via AuthContext)
      await register(displayName.trim() || 'Coach', email.trim(), password);

      // 2. Upload badge if one was selected
      let badge = '';
      if (badgeFile) {
        badge = await uploadFile(badgeFile);
      }

      // 3. Save onboarding data
      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/onboarding`, {
        years_coaching: yearsCoaching,
        coaching_levels: coachingLevels,
        teams_coached: teamsCoached,
        age_groups: ageGroups,
        display_name: displayName.trim() || 'Coach',
        team_name: teamName.trim() || null,
        club_badge: badge || null,
        button_color: primaryColor,
        menu_color_secondary: secondaryColor,
      }, { headers: { Authorization: `Bearer ${token}` } });

      // 4. Create Stripe checkout and redirect
      const origin = window.location.origin;
      const checkoutRes = await axios.post(`${API}/stripe/create-checkout`, {
        plan_id: planId,
        origin_url: origin,
        referral_code: referralCode || null,
      }, { headers: { Authorization: `Bearer ${token}` } });

      localStorage.removeItem(STORAGE_KEY); // Clear saved progress
      window.location.href = checkoutRes.data.url;
    } catch (err) {
      const detail = err.response?.data?.detail || 'Something went wrong';
      toast.error(detail);
      setLoading(false);
    }
  };

  const canProceed = () => {
    if (page > enabledPages.length) return true; // plan page
    const currentPageConfig = enabledPages[page - 1];
    if (!currentPageConfig) return true;
    if (currentPageConfig.type === 'email_capture') {
      // If already registered (e.g. via Google OAuth) just allow advancing
      if (user) return true;
      const v = formData[currentPageConfig.field_key];
      return typeof v === 'string' && /\S+@\S+\.\S+/.test(v.trim());
    }
    if (currentPageConfig.type === 'password_capture') {
      if (user) return true; // already registered, skip the requirement
      const pwField = currentPageConfig.field_key || 'password';
      const pw = formData[pwField] || '';
      const cf = formData[`${pwField}_confirm`] || '';
      return pw.length >= 6 && pw === cf;
    }
    if (!currentPageConfig.required) return true;
    const val = formData[currentPageConfig.field_key];
    if (currentPageConfig.type === 'multi_select') return Array.isArray(val) && val.length > 0;
    if (currentPageConfig.type === 'single_select') return typeof val === 'string' && val.length > 0;
    if (currentPageConfig.type === 'text_input') return typeof val === 'string' && val.trim().length > 0;
    return true;
  };

  // When advancing past the password_capture page, register the account so the
  // user is logged in for the rest of onboarding + plan selection. Idempotent —
  // a user already authenticated (e.g. via Google) just falls through.
  const maybeRegisterBeforeAdvance = async () => {
    if (user) return true;
    const currentPageConfig = enabledPages[page - 1];
    if (!currentPageConfig || currentPageConfig.type !== 'password_capture') return true;

    const pwField = currentPageConfig.field_key || 'password';
    const pw = formData[pwField] || '';
    // Find email from previous email_capture page in the same flow
    const emailPage = enabledPages.find(p => p.type === 'email_capture');
    const emailVal = emailPage ? (formData[emailPage.field_key] || '').trim() : '';
    const nameVal = (formData.display_name || '').trim() || 'Coach';

    if (!emailVal || !pw) return true; // canProceed gates this; just safety

    setLoading(true);
    try {
      await register(nameVal, emailVal, pw);
      return true;
    } catch (err) {
      const detail = err.response?.data?.detail || 'Could not create your account';
      toast.error(detail);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const isLastSetupPage = page === enabledPages.length;

  // Resolve a configured per-option redirect target (single_select pages only)
  // to a 1-indexed page number, or null if no redirect should fire.
  const resolveOptionRedirect = () => {
    const cur = enabledPages[page - 1];
    if (!cur || cur.type !== 'single_select' || !cur.enable_option_redirects) return null;
    const selected = formData[cur.field_key];
    if (!selected) return null;
    const target = (cur.option_redirects || {})[selected];
    if (!target || target === 'next') return null;
    if (target === 'email_capture') {
      const idx = enabledPages.findIndex(p => p.type === 'email_capture');
      if (idx >= 0) return idx + 1;
      return TOTAL_PAGES; // fallback to plan page if email step missing
    }
    if (target === 'plan_page') return TOTAL_PAGES;
    return null;
  };

  const nextPage = async () => {
    if (!canProceed()) {
      toast.error('Please complete this step before continuing');
      return;
    }
    // Register account when leaving the password_capture page (no-op if already authenticated)
    const ok = await maybeRegisterBeforeAdvance();
    if (!ok) return;
    setDirection('forward');
    setLogoSpinKey(k => k + 1);

    // Per-option redirect (single_select pages only) — overrides default linear flow
    const redirectTarget = resolveOptionRedirect();
    if (redirectTarget != null) {
      setPage(redirectTarget);
      return;
    }

    if (isLastSetupPage) {
      if (user) {
        completeOnboarding();
      } else {
        setPage(TOTAL_PAGES);
      }
    } else {
      setPage(p => p + 1);
    }
  };

  const prevPage = () => {
    setDirection('back');
    setLogoSpinKey(k => k + 1);
    setPage(p => Math.max(1, p - 1));
  };

  // Jump straight to the email-capture page (or plan page if email capture is
  // disabled). Used by intro pages that have `show_skip_to_signup` enabled.
  const skipToSignup = () => {
    const emailIdx = enabledPages.findIndex(p => p.type === 'email_capture');
    setDirection('forward');
    setLogoSpinKey(k => k + 1);
    if (emailIdx >= 0) {
      setPage(emailIdx + 1); // 1-indexed
    } else {
      setPage(TOTAL_PAGES); // fallback to plan page
    }
  };

  const toggleMultiSelect = (fieldKey, value) => {
    setFormData(prev => {
      const raw = prev[fieldKey];
      const current = Array.isArray(raw) ? raw : [];
      return {
        ...prev,
        [fieldKey]: current.includes(value) ? current.filter(v => v !== value) : [...current, value],
      };
    });
  };

  // Step indicator - split pages into roughly thirds
  const stepLabels = onboardingConfig?.step_labels || ['Get to know you', 'Setup', 'Choose Plan'];
  const halfPages = Math.ceil(enabledPages.length / 2);
  const currentStep = page <= halfPages ? 1 : page <= enabledPages.length ? 2 : 3;

  // If user already onboarded and has active subscription, redirect.
  // Skip this redirect when ?step=payment is present so admins can preview
  // the embedded payment page without being bounced to /clubhouse.
  useEffect(() => {
    if (searchParams.get('step') === 'payment') return;
    if (user?.is_onboarded && (
      user?.subscription_status === 'active' || 
      user?.subscription_status === 'trialing' ||
      (user?.plan && user.plan !== 'free')
    )) {
      navigate('/clubhouse', { replace: true });
    }
  }, [user, navigate, searchParams]);

  // If onboarded but no subscription, jump to plan page — but ONLY ONCE on
  // initial load. Without the ref, every Back click triggers this effect again
  // and yanks the user back to the plan page (loop trap reported by users).
  const hasJumpedToPlanRef = useRef(false);
  useEffect(() => {
    if (
      !hasJumpedToPlanRef.current &&
      user?.is_onboarded &&
      page < TOTAL_PAGES &&
      TOTAL_PAGES > 1 &&
      !searchParams.get('cancelled')
    ) {
      hasJumpedToPlanRef.current = true;
      setPage(TOTAL_PAGES);
    }
  }, [user, page, searchParams, TOTAL_PAGES]);

  const theme = localStorage.getItem('landingTheme') || 'dark';

  // Wait for config to load
  if (!onboardingConfig) {
    return (
      <div className="auth-container" data-theme={theme}>
        <div className="flex items-center justify-center h-screen">
          <div className="w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  const animationsEnabled = onboardingConfig?.animations_enabled !== false;
  const slideClass = animationsEnabled
    ? (direction === 'back' ? 'animate-onbSlideInFromRight' : 'animate-onbSlideInFromLeft')
    : '';

  // When on the color picker page AND user has actually picked a colour, tint the bg
  const currentPageType = enabledPages[page - 1]?.type;
  const rawPrimary = formData.club_colours?.primary;
  const hasPrimaryPick = currentPageType === 'color_picker' && !!rawPrimary;
  const onWhiteBg = hasPrimaryPick && rawPrimary.toUpperCase() === '#F8FAFC';
  const containerStyle = hasPrimaryPick
    ? { background: rawPrimary, transition: 'background 0.4s ease' }
    : theme === 'light'
      ? { background: '#FFFFFF', transition: 'background 0.4s ease' }
      : { transition: 'background 0.4s ease' };

  // Darker, heavier tint of the chosen primary — used for the content card so
  // it visually reacts to the user's selection while keeping text readable.
  const onBlackBg = hasPrimaryPick && rawPrimary.toUpperCase() === '#000000';
  // True when the page bg is a "light" colour and outside-the-card elements need DARK contrast
  const isLightPagebg = hasPrimaryPick && hexBrightness(rawPrimary) > 0.55;
  const cardStyle = hasPrimaryPick
    ? {
        backgroundColor: isLightPagebg
          ? '#1F2937'
          : onBlackBg
            ? '#1F2937'
            : darkenHex(rawPrimary, 0.3),
        transition: 'background-color 0.4s ease',
      }
    : { transition: 'background-color 0.4s ease' };
  // When the card is colour-tinted, flip text colours so the form stays readable.
  const cardTextLight = hasPrimaryPick;

  return (
    <div className="auth-container onboarding-container" data-theme={theme} style={containerStyle}>
      <div className="w-full max-w-md px-4 flex flex-col" style={{ minHeight: '100dvh' }}>
        {/* HEADER — logo + KO/HT/FT football tracker (pinned to top) */}
        <OnboardingHeader
          theme={theme}
          page={page}
          TOTAL_PAGES={TOTAL_PAGES}
          enabledPagesLength={enabledPages.length}
          hasPrimaryPick={hasPrimaryPick}
          isLightPagebg={isLightPagebg}
          animationsEnabled={animationsEnabled}
          direction={direction}
          logoSpinKey={logoSpinKey}
        />

        {/* MAIN — current page content inside (mostly transparent) card */}
        <div className="flex-1 flex items-start sm:items-center justify-center w-full pt-0 sm:py-2 pb-2 overflow-y-auto overflow-x-hidden">
          <Card
            className={`auth-card border-0 shadow-none animate-fadeIn relative z-10 w-full ${
              (hasPrimaryPick || enabledPages[page - 1]?.keep_card_bg_mobile) ? 'onb-card-keep-bg' : ''
            }`}
            style={cardStyle}
          >
            <CardContent className="pt-0 sm:pt-6 pb-6 px-0 sm:px-6">
              <div key={`onb-page-${page}`} className={slideClass}>
                {/* Dynamic page (intro / counter / multi/single-select / text / file / colour / features) */}
                {page <= enabledPages.length && (
                  <OnboardingPage
                    pg={enabledPages[page - 1]}
                    fieldVal={formData[enabledPages[page - 1]?.field_key]}
                    formData={formData}
                    cardTextLight={cardTextLight}
                    darkBg={cardTextLight || theme === 'dark'}
                    primaryColor={primaryColor}
                    secondaryColor={secondaryColor}
                    badgeFile={badgeFile}
                    badgePreview={badgePreview}
                    setField={setField}
                    setPrimaryColor={setPrimaryColor}
                    toggleMultiSelect={toggleMultiSelect}
                    handleFileSelect={handleFileSelect}
                    removeBadge={removeBadge}
                    skipToSignup={skipToSignup}
                    showSkipLink={!user}
                  />
                )}

                {/* Final page — Plan selection */}
                {/* Final page — Plan selection (hosted-redirect flow).
                    Skipped when payment_capture is enabled in CMS — that page
                    handles plan selection + embedded payment in one step. */}
                {page === TOTAL_PAGES && !hasEmbeddedPayment && (
                  <PlanSelection
                    displayName={displayName || user?.display_name || user?.name}
                    yearsCoaching={yearsCoaching || user?.years_coaching}
                    plans={plans}
                    onSelectPlan={user ? selectPlan : handlePublicPlanSelection}
                    loading={loading}
                    isAuthenticated={!!user}
                    email={email}
                    setEmail={setEmail}
                    password={password}
                    setPassword={setPassword}
                    confirmPassword={confirmPassword}
                    setConfirmPassword={setConfirmPassword}
                    planPageConfig={onboardingConfig?.plan_page}
                    onSignOut={user ? () => { logout(); navigate('/', { replace: true }); } : null}
                  />
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* FOOTER — Back / Next / Skip + page dots (pinned to bottom) */}
        <OnboardingFooter
          page={page}
          TOTAL_PAGES={TOTAL_PAGES}
          enabledPages={enabledPages}
          enabledPagesLength={enabledPages.length}
          loading={loading}
          hasPrimaryPick={hasPrimaryPick}
          isLightPagebg={isLightPagebg}
          cardStyle={cardStyle}
          canProceed={canProceed}
          nextPage={nextPage}
          prevPage={prevPage}
          navigate={navigate}
          isLastSetupPage={isLastSetupPage}
          user={user}
          completeOnboarding={completeOnboarding}
          setPage={setPage}
        />
      </div>
    </div>
  );
}

